import React, { useEffect, useState, useMemo } from 'react';
import { getEffectiveConfig } from '../../services/api';

/**
 * PEP-5 — Effective-Config panel for governance canvas nodes.
 *
 * Shows the operator: "what will this node actually do at runtime?"
 * by rendering the three policy layers + the resolved effective set,
 * all sourced from /api/v1/governance/effective-config.
 *
 * Mounted by V3Inspector for governance-family node types (dpg_*,
 * policy_*, agent_autonomy_gate, token_vault, vault_resolve,
 * audit_record, audit_event, policy_audit, compliance_scoring).
 *
 * Design principles:
 *   • Read-only — never mutates node config; the existing Inspector
 *     panels above handle editing.
 *   • Honest about what's honored vs decoration — the `unhonoredKeys`
 *     section surfaces FE template fields the BE does NOT yet read at
 *     runtime, so operators don't form false expectations.
 *   • V2 design tokens only; no inline hex, no hardcoded colors.
 *
 * Props:
 *   workflowId — workflow UUID (from V3Inspector context)
 *   nodeId     — canvas node id (string)
 */
function Chip({ tone, children }) {
  const toneStyle = {
    neutral: { background: 'var(--v2-bg-muted)',    color: 'var(--v2-text-secondary)', border: '1px solid var(--v2-border-default)' },
    accent:  { background: 'var(--v2-action-soft)', color: 'var(--v2-action-strong)',   border: '1px solid var(--v2-action-soft)' },
    warn:    { background: 'var(--v2-warning-soft)', color: 'var(--v2-warning-strong)', border: '1px solid var(--v2-warning-soft)' },
    danger:  { background: 'var(--v2-danger-soft)', color: 'var(--v2-danger-strong)',   border: '1px solid var(--v2-danger-soft)' },
    success: { background: 'var(--v2-success-soft)', color: 'var(--v2-success-strong)', border: '1px solid var(--v2-success-soft)' },
  }[tone || 'neutral'];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 600,
      ...toneStyle,
    }}>{children}</span>
  );
}

function Section({ title, subtitle, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ borderTop: '1px solid var(--v2-border-default)', padding: '10px 0' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <div style={{ textAlign: 'left' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--v2-text-primary)' }}>{title}</div>
          {subtitle && (
            <div style={{ fontSize: 11, color: 'var(--v2-text-secondary)', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        <span style={{ fontSize: 10, color: 'var(--v2-text-disabled)' }}>{open ? '▾' : '▸'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function actionTone(action) {
  switch ((action || '').toUpperCase()) {
    case 'BLOCK':    return 'danger';
    case 'REDACT':
    case 'TOKENIZE':
    case 'MASK':     return 'warn';
    case 'LOG':      return 'neutral';
    default:         return 'neutral';
  }
}

function RuleRow({ rule, showEffective }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--v2-bg-muted)' }}>
      <td style={{ padding: '6px 8px', fontWeight: 600, color: 'var(--v2-text-primary)' }}>{rule.ruleName || '(unnamed rule)'}</td>
      <td style={{ padding: '6px 8px' }}>
        <Chip tone="neutral">{rule.category}</Chip>
      </td>
      <td style={{ padding: '6px 8px', color: 'var(--v2-text-secondary)', fontSize: 11 }}>{rule.sensitivityLevel}</td>
      {showEffective && (
        <td style={{ padding: '6px 8px' }}>
          <Chip tone={actionTone(rule.effectiveAction || rule.action)}>{rule.effectiveAction || rule.action}</Chip>
          {rule.actionOverridden && (
            <span style={{ marginLeft: 6, fontSize: 10, color: 'var(--v2-text-disabled)' }}>
              (was {rule.action})
            </span>
          )}
        </td>
      )}
      {!showEffective && (
        <td style={{ padding: '6px 8px' }}>
          <Chip tone={actionTone(rule.action)}>{rule.action}</Chip>
        </td>
      )}
    </tr>
  );
}

function DroppedRow({ rule }) {
  return (
    <tr style={{ borderBottom: '1px solid var(--v2-bg-muted)' }}>
      <td style={{ padding: '6px 8px', color: 'var(--v2-text-disabled)', textDecoration: 'line-through' }}>{rule.ruleName || '(unnamed rule)'}</td>
      <td style={{ padding: '6px 8px' }}>
        <Chip tone="neutral">{rule.category}</Chip>
      </td>
      <td style={{ padding: '6px 8px', color: 'var(--v2-text-disabled)' }} colSpan={2}>
        <span style={{ fontSize: 11 }}>
          dropped by <strong>{rule.droppedBy}</strong>: {rule.droppedReason}
        </span>
      </td>
    </tr>
  );
}

export default function EffectiveConfigPanel({ workflowId, nodeId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Re-fetch on workflow/node change. Cheap endpoint; no debounce needed
  // (Inspector only mounts this when the selection is a governance type).
  useEffect(() => {
    if (!workflowId || !nodeId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    getEffectiveConfig(workflowId, nodeId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load effective config'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowId, nodeId]);

  const hasNodeOverride = useMemo(
    () => Boolean(data && data.nodeOverride && data.nodeOverride.active),
    [data]
  );
  const hasWorkflowOverride = useMemo(
    () => Boolean(data && data.workflowOverride && data.workflowOverride.active),
    [data]
  );

  if (loading) {
    return (
      <div style={{ padding: 12, color: 'var(--v2-text-secondary)', fontSize: 12 }}>
        Loading effective config…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 12, color: 'var(--v2-danger-strong)', fontSize: 12, background: 'var(--v2-danger-soft)', borderRadius: 6 }}>
        Could not load effective config: {error}
      </div>
    );
  }
  if (!data || data.error === 'node_not_found') {
    return (
      <div style={{ padding: 12, color: 'var(--v2-text-disabled)', fontSize: 12 }}>
        Effective config unavailable for this node.
        {data?.reason && <div style={{ marginTop: 4 }}>{data.reason}</div>}
      </div>
    );
  }

  return (
    <div style={{
      padding: '12px',
      border: '1px solid var(--v2-border-default)',
      borderRadius: 8,
      background: 'var(--v2-bg-surface)',
    }}>
      {/* Summary chips */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center', marginBottom: 8 }}>
        <Chip tone="success">{data.effectiveRuleCount} effective</Chip>
        {data.droppedRuleCount > 0 && <Chip tone="neutral">{data.droppedRuleCount} dropped</Chip>}
        {hasWorkflowOverride && <Chip tone="accent">Workflow override</Chip>}
        {hasNodeOverride && <Chip tone="accent">Node override</Chip>}
        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'var(--v2-text-disabled)' }}>
          {data.tenantRuleCount} tenant rules
        </span>
      </div>

      {/* Node override layer — operator-set, what they actually changed */}
      {hasNodeOverride && (
        <Section
          title="Node override"
          subtitle="Per-node config set on this Inspector (or by Protect auto-insert)"
          defaultOpen
        >
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <tbody>
              {data.nodeOverride.scanMode && (
                <tr>
                  <td style={{ padding: '3px 8px', color: 'var(--v2-text-secondary)' }}>scanMode</td>
                  <td style={{ padding: '3px 8px' }}><Chip tone="accent">{data.nodeOverride.scanMode}</Chip></td>
                </tr>
              )}
              {data.nodeOverride.forcedAction && (
                <tr>
                  <td style={{ padding: '3px 8px', color: 'var(--v2-text-secondary)' }}>failAction</td>
                  <td style={{ padding: '3px 8px' }}><Chip tone={actionTone(data.nodeOverride.forcedAction)}>{data.nodeOverride.forcedAction}</Chip></td>
                </tr>
              )}
              {data.nodeOverride.patterns && data.nodeOverride.patterns.length > 0 && (
                <tr>
                  <td style={{ padding: '3px 8px', color: 'var(--v2-text-secondary)' }}>patterns</td>
                  <td style={{ padding: '3px 8px' }}>
                    {data.nodeOverride.patterns.map((p) => (
                      <span key={p} style={{ marginRight: 4 }}><Chip tone="neutral">{p}</Chip></span>
                    ))}
                  </td>
                </tr>
              )}
              {data.nodeOverride.minSensitivity != null && data.nodeOverride.minSensitivity > 0 && (
                <tr>
                  <td style={{ padding: '3px 8px', color: 'var(--v2-text-secondary)' }}>minSensitivity</td>
                  <td style={{ padding: '3px 8px' }}><Chip tone="neutral">≥ {data.nodeOverride.minSensitivity}</Chip></td>
                </tr>
              )}
            </tbody>
          </table>
        </Section>
      )}

      {/* Workflow override layer */}
      {hasWorkflowOverride && (
        <Section
          title="Workflow override"
          subtitle={`${data.workflowOverride.disabledRuleIds.length} rule(s) disabled for this workflow`}
          defaultOpen={false}
        >
          {data.workflowOverride.reason && (
            <div style={{ fontSize: 11, color: 'var(--v2-text-secondary)', marginBottom: 4 }}>
              Reason: <em>{data.workflowOverride.reason}</em>
            </div>
          )}
        </Section>
      )}

      {/* Effective rule set */}
      <Section
        title={`Effective rules (${data.effectiveRuleCount})`}
        subtitle="Rules that WILL evaluate at this node, with effective action after override"
        defaultOpen
      >
        {data.effective && data.effective.length > 0 ? (
          <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid var(--v2-bg-muted)', borderRadius: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead style={{ background: 'var(--v2-bg-elevated)' }}>
                <tr>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Rule</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Category</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Sens.</th>
                  <th style={{ textAlign: 'left', padding: '6px 8px', fontWeight: 700 }}>Effective</th>
                </tr>
              </thead>
              <tbody>
                {data.effective.map((r) => <RuleRow key={r.ruleId} rule={r} showEffective />)}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: 8, color: 'var(--v2-text-disabled)', fontSize: 11 }}>
            No rules will evaluate at this node.
          </div>
        )}
      </Section>

      {/* Dropped rules */}
      {data.dropped && data.dropped.length > 0 && (
        <Section
          title={`Dropped (${data.droppedRuleCount})`}
          subtitle="Tenant rules excluded by workflow or node override"
          defaultOpen={false}
        >
          <div style={{ maxHeight: 160, overflowY: 'auto', border: '1px solid var(--v2-bg-muted)', borderRadius: 4 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <tbody>
                {data.dropped.map((r) => <DroppedRow key={r.ruleId} rule={r} />)}
              </tbody>
            </table>
          </div>
        </Section>
      )}

      {/* Honesty surface — fields written but not honored at runtime */}
      {data.unhonoredKeys && data.unhonoredKeys.length > 0 && (
        <Section
          title={`Decorative config (${data.unhonoredKeys.length})`}
          subtitle="Fields on this node that the BE does not yet honor at runtime"
          defaultOpen={false}
        >
          <div style={{ fontSize: 11 }}>
            {data.unhonoredKeys.map((k) => (
              <div key={k.key} style={{ padding: '4px 0', borderBottom: '1px solid var(--v2-bg-muted)' }}>
                <Chip tone="warn">{k.key}</Chip>
                <span style={{ marginLeft: 8, color: 'var(--v2-text-secondary)' }}>{k.note}</span>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
